import { useState } from "react";
import { useDateFilter } from "../App";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const presetOptions = [
  { value: 'today', label: 'Hoy' },
  { value: 'yesterday', label: 'Ayer' },
  { value: 'last7days', label: 'Últimos 7 días' },
  { value: 'last30days', label: 'Últimos 30 días' },
  { value: 'thisMonth', label: 'Este Mes' },
  { value: 'lastMonth', label: 'Mes Pasado' },
  { value: 'last3months', label: 'Últimos 3 meses' },
  { value: 'last6months', label: 'Últimos 6 meses' },
  { value: 'thisYear', label: 'Este Año' },
  { value: 'lastYear', label: 'Año Pasado' },
];

const DateRangePicker = () => {
  const { startDate, endDate, selectedPreset, presetLabel, applyPreset, applyCustomRange } = useDateFilter();
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const handlePresetSelect = (preset) => {
    applyPreset(preset);
    setShowCustom(false);
    setIsOpen(false);
  };

  const handleCustomApply = () => {
    if (tempStartDate && tempEndDate) {
      applyCustomRange(tempStartDate, tempEndDate);
      setShowCustom(false);
      setIsOpen(false);
    }
  };

  const formatDateRange = () => {
    if (selectedPreset === 'custom') {
      return `${format(startDate, "dd MMM", { locale: es })} - ${format(endDate, "dd MMM yyyy", { locale: es })}`;
    }
    return presetLabel;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="bg-white border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2] min-w-[180px] justify-between"
          data-testid="date-range-picker"
        >
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-[#F43F5E]" />
            <span className="text-sm">{formatDateRange()}</span>
          </div>
          <ChevronDown className="w-4 h-4 text-[#6B7280]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[95vw] sm:w-auto p-0 bg-white border-[#FECDD3]" align="start" side="bottom" sideOffset={5}>
        <div className="max-h-[75vh] overflow-y-auto">
          <div className="flex flex-col sm:flex-row">
            {/* Preset options */}
            <div className="border-b sm:border-b-0 sm:border-r border-[#FECDD3] p-2">
              <p className="text-xs text-[#6B7280] px-2 py-1 font-medium">Período</p>
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-1">
                {presetOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handlePresetSelect(option.value)}
                    className={`w-full text-left px-3 py-1.5 sm:py-2 text-sm rounded-md transition-colors ${
                      selectedPreset === option.value
                        ? 'bg-[#F43F5E] text-white'
                        : 'text-[#4A4A4A] hover:bg-[#FEE2E2]'
                    }`}
                    data-testid={`preset-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <hr className="my-2 border-[#FECDD3]" />
              <button
                onClick={() => {
                  setShowCustom(true);
                  setTempStartDate(startDate);
                  setTempEndDate(endDate);
                }}
                className={`w-full text-left px-3 py-1.5 sm:py-2 text-sm rounded-md transition-colors ${
                  selectedPreset === 'custom'
                    ? 'bg-[#F43F5E] text-white'
                    : 'text-[#4A4A4A] hover:bg-[#FEE2E2]'
                }`}
                data-testid="preset-custom"
              >
                Personalizado
              </button>
            </div>

            {/* Custom date picker */}
            {showCustom && (
              <div className="p-3 sm:p-4">
                <p className="text-xs text-[#6B7280] mb-2 font-medium">Selecciona el rango</p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Desde</p>
                    <Calendar
                      mode="single"
                      selected={tempStartDate}
                      onSelect={setTempStartDate}
                      locale={es}
                      className="rounded-md border border-[#FECDD3]"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Hasta</p>
                    <Calendar
                      mode="single"
                      selected={tempEndDate}
                      onSelect={setTempEndDate}
                      locale={es}
                      className="rounded-md border border-[#FECDD3]"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3 sm:mt-4 sticky bottom-0 bg-white py-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCustom(false)}
                  className="border-[#FECDD3] text-[#6B7280]"
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleCustomApply}
                  className="bg-[#F43F5E] hover:bg-[#F43F5E]/80 text-white"
                  disabled={!tempStartDate || !tempEndDate}
                >
                  Aplicar
                </Button>
              </div>
            </div>
          )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
